// ============================================================================
// TemplateEditor — the create / edit Drawer for a program. Name the program,
// add exercises (via the ExercisePicker), set per-item default sets / reps /
// weight, reorder with the arrow controls, and remove. Saves through
// useCreateTemplate / useUpdateTemplate; on success the list refetches and the
// live-sync socket keeps every device in step.
//
// Optional-first: a blank item field is dropped from the payload; only the name
// is required client-side. Numeric defaults stay as strings in local state so
// the inputs can be empty.
// ============================================================================
import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Banner, Button, TextField } from "../../components";
import { useCreateTemplate, useUpdateTemplate } from "../../hooks/templates";
import { useExercises } from "../../hooks/exercises";
import type { Exercise, Template } from "../../api";
import { Drawer } from "./Drawer";
import { ExercisePicker } from "./ExercisePicker";
import { IconArrowDown, IconArrowUp, IconGrip, IconPlus, IconTrash } from "./icons";
import {
  draftFromExercise,
  draftsFromTemplate,
  moveItem,
  toTemplateInput,
} from "./lib";
import type { DraftItem } from "./lib";

interface TemplateEditorProps {
  open: boolean;
  onClose: () => void;
  /** Existing template to edit, or undefined to create a new one. */
  template?: Template;
}

export function TemplateEditor({ open, onClose, template }: TemplateEditorProps) {
  const { t, i18n } = useTranslation("templates");
  const locale = i18n.language;
  const reduce = useReducedMotion();

  const create = useCreateTemplate();
  const update = useUpdateTemplate();
  const saving = create.isPending || update.isPending;
  const saveError = create.isError || update.isError;

  // The catalog feeds name/meta resolution when hydrating an existing template.
  const catalogQuery = useExercises();
  const catalog = useMemo(() => {
    const map = new Map<number, Exercise>();
    for (const ex of catalogQuery.data ?? []) map.set(ex.id, ex);
    return map;
  }, [catalogQuery.data]);

  const [name, setName] = useState("");
  const [items, setItems] = useState<DraftItem[]>([]);
  const [nameError, setNameError] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // (Re)seed the editor whenever it opens or the catalog hydrates.
  useEffect(() => {
    if (!open) return;
    setNameError(false);
    create.reset();
    update.reset();
    if (template) {
      setName(template.name ?? "");
      setItems(draftsFromTemplate(template, catalog, locale));
    } else {
      setName("");
      setItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template, catalog]);

  const existingIds = useMemo(
    () => new Set(items.map((it) => it.exerciseId)),
    [items],
  );

  const setField = (key: string, field: keyof DraftItem, value: string) =>
    setItems((prev) =>
      prev.map((it) => (it.key === key ? { ...it, [field]: value } : it)),
    );

  const addExercise = (ex: Exercise) =>
    setItems((prev) =>
      prev.some((it) => it.exerciseId === ex.id)
        ? prev
        : [...prev, draftFromExercise(ex, locale)],
    );

  const removeItem = (key: string) =>
    setItems((prev) => prev.filter((it) => it.key !== key));

  const move = (index: number, dir: -1 | 1) =>
    setItems((prev) => moveItem(prev, index, dir));

  const submit = () => {
    if (!name.trim()) {
      setNameError(true);
      return;
    }
    setNameError(false);
    const body = toTemplateInput(name, items);
    const onSuccess = () => onClose();
    if (template) {
      update.mutate({ id: template.id, body }, { onSuccess });
    } else {
      create.mutate(body, { onSuccess });
    }
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={template ? t("editor.editTitle") : t("editor.newTitle")}
        subtitle={template ? t("editor.editSubtitle") : t("editor.newSubtitle")}
        footer={
          <div className="flex items-center justify-end gap-3">
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              {t("actions.cancel")}
            </Button>
            <Button onClick={submit} disabled={saving}>
              {saving ? t("actions.saving") : t("actions.save")}
            </Button>
          </div>
        }
      >
        <form
          className="space-y-6"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <TextField
            label={t("editor.name")}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError(false);
            }}
            placeholder={t("editor.namePlaceholder")}
            required
            error={nameError ? t("editor.nameRequired") : undefined}
            maxLength={120}
          />

          <section>
            <div className="mb-3 flex items-end justify-between gap-3">
              <div>
                <h3 className="font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
                  {t("editor.exercisesTitle")}
                </h3>
                {items.length > 1 && (
                  <p className="mt-1 text-xs text-body-faint">
                    {t("editor.exercisesSubtitle")}
                  </p>
                )}
              </div>
              <Button
                size="sm"
                variant="ghost"
                type="button"
                onClick={() => setPickerOpen(true)}
                iconLeft={<IconPlus width={16} height={16} />}
              >
                {t("actions.addExercise")}
              </Button>
            </div>

            {items.length === 0 ? (
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-line-strong bg-surface-sunken/60 px-6 py-10 text-center transition-colors hover:border-coral/50 hover:bg-coral-soft/50"
              >
                <span className="grid h-11 w-11 place-items-center rounded-pill bg-grad-coral text-white shadow-volt">
                  <IconPlus width={20} height={20} />
                </span>
                <span className="font-display text-sm font-bold text-body">
                  {t("editor.noItems")}
                </span>
                <span className="text-xs text-body-soft">{t("editor.noItemsBlurb")}</span>
              </button>
            ) : (
              <ul className="space-y-2.5">
                <AnimatePresence initial={false}>
                  {items.map((it, i) => (
                    <ItemRow
                      key={it.key}
                      item={it}
                      index={i}
                      total={items.length}
                      reduce={!!reduce}
                      onField={(field, value) => setField(it.key, field, value)}
                      onMove={(dir) => move(i, dir)}
                      onRemove={() => removeItem(it.key)}
                    />
                  ))}
                </AnimatePresence>
              </ul>
            )}
          </section>

          {saveError && (
            <Banner tone="danger" title={t("editor.saveError")}>
              {t("editor.saveErrorBlurb")}
            </Banner>
          )}
        </form>
      </Drawer>

      <ExercisePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        existingIds={existingIds}
        onAdd={addExercise}
      />
    </>
  );
}

// --- a single reorderable, editable item row --------------------------------

function ItemRow({
  item,
  index,
  total,
  reduce,
  onField,
  onMove,
  onRemove,
}: {
  item: DraftItem;
  index: number;
  total: number;
  reduce: boolean;
  onField: (field: keyof DraftItem, value: string) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation("templates");
  const name = item.name || t("item.unnamed");

  return (
    <motion.li
      layout={!reduce}
      initial={reduce ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.22, ease: [0.22, 0.61, 0.36, 1] }}
      className="rounded-lg bg-surface p-3 ring-1 ring-line shadow-xs"
    >
      <div className="flex items-start gap-2.5">
        {/* Position badge + reorder controls */}
        <div className="flex flex-col items-center gap-1 pt-0.5">
          <span
            className="grid h-6 w-6 shrink-0 place-items-center rounded-pill bg-grad-violet text-[0.7rem] font-extrabold tabular-nums text-white shadow-ember"
            aria-hidden
          >
            {index + 1}
          </span>
          <span className="text-body-faint" aria-hidden>
            <IconGrip width={16} height={16} />
          </span>
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-sm font-bold text-body">{name}</p>
          {item.meta && <p className="truncate text-xs text-body-soft">{item.meta}</p>}

          {/* Defaults */}
          <div className="mt-2.5 grid grid-cols-3 gap-2">
            <NumField
              label={t("item.sets")}
              value={item.sets}
              onChange={(v) => onField("sets", v)}
              max={99}
            />
            <NumField
              label={t("item.reps")}
              value={item.reps}
              onChange={(v) => onField("reps", v)}
              max={999}
            />
            <NumField
              label={t("item.weight")}
              value={item.weight}
              onChange={(v) => onField("weight", v)}
              decimal
              suffix="kg"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex shrink-0 flex-col items-center gap-1">
          <IconBtn
            label={t("actions.moveUp")}
            onClick={() => onMove(-1)}
            disabled={index === 0}
          >
            <IconArrowUp width={16} height={16} />
          </IconBtn>
          <IconBtn
            label={t("actions.moveDown")}
            onClick={() => onMove(1)}
            disabled={index === total - 1}
          >
            <IconArrowDown width={16} height={16} />
          </IconBtn>
          <IconBtn label={t("item.remove")} onClick={onRemove} danger>
            <IconTrash width={16} height={16} />
          </IconBtn>
        </div>
      </div>
    </motion.li>
  );
}

function NumField({
  label,
  value,
  onChange,
  decimal = false,
  suffix,
  max,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  decimal?: boolean;
  suffix?: string;
  max?: number;
}) {
  return (
    <TextField
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      type="number"
      inputMode={decimal ? "decimal" : "numeric"}
      step={decimal ? "0.5" : "1"}
      min={0}
      max={max}
      suffix={suffix}
      className="h-10 text-sm"
    />
  );
}

function IconBtn({
  label,
  onClick,
  disabled = false,
  danger = false,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={
        "grid h-7 w-7 place-items-center rounded-md text-body-muted transition-colors disabled:pointer-events-none disabled:opacity-30 " +
        (danger ? "hover:bg-coral-soft hover:text-coral" : "hover:bg-surface-sunken hover:text-body")
      }
    >
      {children}
    </button>
  );
}
